import React, { useState, useEffect } from "react";
import {
  IconButton,
  Avatar,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Dialog,
  Box,
  Divider,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import CloseIcon from "@mui/icons-material/Close";

import { StarIcon } from "lucide-react";
import { StarRounded } from "@mui/icons-material";
import PWAHeader from "../../common/PWAHeader";
import { fetchMoreVideoHomeComponents } from "../../apiservices/ApiService";
const StreamerProfile = ({ typeId, type, displayName, onBack }) => {
  const [cards, setCategoryCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Fetching data indicator
  const [loading, setLoading] = useState(false); // Loading video player indicator
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // Check if there is more data to load

  const handleClosePlayer = () => {
    setSelectedVideo(null);
  };

  const loadCategoryVideos = async (page = 1) => {
    if (isLoading || !hasMore) return; // Prevent duplicate requests or unnecessary calls

    setIsLoading(true);
    try {
      const data = await fetchMoreVideoHomeComponents({ typeId, type, pageId: page });
      if (data?.cards) {
        setCategoryCards((prevCards) => [...prevCards, ...data.cards]); // Append new cards
        setHasMore(data.cards.length > 0); // Check if more data exists
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching category videos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fixUrlProtocol = (url) => {
    if (!url) return '';
    return url.startsWith('http:') ? url.replace(/^http:/, 'https:') : url;
  };
  useEffect(() => {
    loadCategoryVideos(1); // Load the first page on component mount
  }, [typeId, type]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 400
      ) {
        if (!isLoading && hasMore) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          loadCategoryVideos(nextPage);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [currentPage, isLoading, hasMore]);

  const handleVideoClick = (videoData) => {
    setSelectedVideo(videoData);
  };

  const renderCardType35 = (card) => {
    if (!card?.cardData || card.cardData.length === 0) return null; // Check if cardData exists

    return (
      <Box sx={{ marginBottom: 4}}>
        {card.cardMetaData?.title && (
          <Typography variant="h6" sx={{ marginBottom: 2, color: "#771615" }}>
            {card.cardMetaData.title} 
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {card.cardData.map((item, idx) => (
            <Card
              key={idx}
              onClick={() => handleVideoClick(item.data)}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                cursor: "pointer",
                background: "linear-gradient(to top, #fff 0%, #FF9800 100%)",
                // border: "1px solid #771615",
              }}
            >
              <CardMedia
                component="img"
                image={item.data.thumbnail}
                alt={item.data.title}
                sx={{
                  height: 180,
                  objectFit: "cover",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 10,
                  left: 10,
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: "12px",
                  background: "#771615",
                }}
              >
                {item.data.duration_display}
              </Box>
              <CardContent>
                <Typography variant="body1" fontWeight="bold" color="#771615" gutterBottom>
                  {item.data.title}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginTop: 1,
                  }}
                >
                  <Avatar
                    src= {fixUrlProtocol(item.data.streamerImageUrl)}


                    alt={item.data.streamerName}
                    sx={{ width: 30, height: 30 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {item.data.streamerName}
                  </Typography>
                  <Typography variant="body2" sx={{ marginLeft: "auto", color: "#771615" }}>
                    <span>👁 {item.data.view_count_display}</span> •{" "}
                    <span>❤️ {item.data.like_count_display}</span> •{" "}
                    <span>💬 {item.data.comment_count_display}</span>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  };

  const renderProfileSection = (card) => {
    const profileData = card.cardData[0]?.data; // Extract profile data from the card
    if (!profileData) return null; // If no data is found, return null

    return (
      <Card
        sx={{
          maxWidth: 400,
          margin: "20px auto",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)", // Updated for modern look
          background: "linear-gradient(to top, #fff 0%, #FF9800 100%)", // Material theme color
          overflow: "hidden",
        }}
      >
        <CardContent>
          <Box sx={{ textAlign: "center", marginBottom: 2 }}>
            <Avatar
              src={profileData.streamerImage}
              alt={profileData.displayName}
              sx={{
                width: 120,
                height: 120,
                margin: "0 auto",
                border: "3px solid",
                borderColor: "#771615",
              }}
            />
          </Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            color="#771615"
            gutterBottom
          >
            {profileData.prefix} {profileData.firstName} {profileData.lastName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Typography
              variant="body1"
              // color="textSecondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#771615" }}
            >
            <StarRounded /> <strong>{profileData.rating}</strong>
            </Typography>
          </Box>
          <Divider sx={{ marginY: 2 }} />
          <Typography
            variant="body2"
            color="#771615"
            textAlign="center"
            sx={{ marginBottom: 2, paddingX: 2 }}
          >
            {profileData.description}
          </Typography>
          <Divider sx={{ marginY: 2 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              textAlign: "center",
              paddingX: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                color="#771615"
                fontWeight="bold"
              >
                {profileData.views}
              </Typography>
              <Typography variant="body2" color="#771615">
                Views
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="h6"
                color="#771615"
                fontWeight="bold"
              >
                {profileData.likes}
              </Typography>
              <Typography variant="body2" color="#771615">
                Likes
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="h6"
                color="#771615"
                fontWeight="bold"
              >
                {profileData.followers}
              </Typography>
              <Typography variant="body2" color="#771615">
                Followers
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderCardsByTypeAndItem = (card) => {
    if (!card) return null; // Ensure card exists
    switch (card.keyword) {
      case "videos-by-streamer":
        return renderCardType35(card);
      case "streamer-profile":
        return renderProfileSection(card);
      default:
        return null;
    }
  };

  return (
    <>
      {/* <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "10px 16px",
          backgroundColor: "#fff",
          borderBottom: "1px solid #ddd",
        }}
      >
        <ArrowBackIosIcon
          sx={{ cursor: "pointer" }}
          onClick={onBack}
        />
        <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center" }}>
          {displayName}
        </Typography>
      </Box> */}

        <PWAHeader title={displayName} onBack={onBack} />

      <Box
        sx={{
          backgroundColor: "rgba(255, 152, 0, 0.11)",
          width: "100%",
          overflowX: "hidden",
          padding: 2,
          boxSizing: "border-box",
        }}
      >
        {cards.map((card, idx) => (
          <Box key={idx}>{renderCardsByTypeAndItem(card)}</Box>
        ))}
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
      {selectedVideo && (
        <Dialog
          open={Boolean(selectedVideo)}
          onClose={handleClosePlayer}
          fullScreen
          sx={{
            "& .MuiPaper-root": {
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.3)",
              borderRadius: "0px",
              backgroundColor: "#000",
            },
          }}
        >
          <Box sx={{ position: "relative", height: "100%" }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
              >
                <CircularProgress color="inherit" />
              </Box>
            )}
            <video
              controls
              autoPlay
              src={selectedVideo.streamUrl}
              onLoadedData={() => setLoading(false)}
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "0px",
                display: loading ? "none" : "block",
              }}
            />
            <Typography
              variant="body1"
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                color: "#fff",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                padding: "8px 16px",
                borderRadius: 2,
                zIndex: 5,
                maxWidth: "70%",
              }}
            >
              {selectedVideo?.title}
            </Typography>
            <IconButton
              onClick={handleClosePlayer}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 5,
                color: "#fff",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default StreamerProfile;